import { OpenInNew } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { EDIT_APPOINTMENT } from "../../../enums/PermissionTitles";
import { APPOINTEMENT_DETAILS } from "../../../enums/UrlPaths";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";

export default function SingleBiometricDetails({ data }) {
  const { t } = useTranslation();
  const methods = useFormContext();
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const { roles } = useUser();

  React.useEffect(() => {
    if (data) {
      methods.reset({
        temperature: data.temperature ?? "",
        heartRate: data.heartRate ?? "",
        weight: data.weight ?? "",
        length: data.length ?? "",
        height: data.height ?? "",
        breathingFrequency: data.breathingFrequency ?? "",
      });
    }
  }, [data]);

  const hasAppointment = data?.appointment ? true : false;
  const appointmentId = data?.appointment?.id;

  if (roles.user) {
    return (
      <>
        <Grid container spacing={2} mb={3}>
          {[
            {
              label: t("BodyTemperature"),
              value: data?.temperature,
              testIdKey: "temperature",
            },
            {
              label: t("Puls/Min"),
              value: data?.heartRate,
              testIdKey: "heart-rate",
            },
            { label: t("Mass"), value: data?.weight, testIdKey: "weight" },
            {
              label: t("FrequencyOfBreathing"),
              value: data?.breathingFrequency,
              testIdKey: "breathing-frequency",
            },
            { label: t("length"), value: data?.length, testIdKey: "length" },
            { label: t("height"), value: data?.height, testIdKey: "height" },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Typography variant="subtitle1">
                <strong>{item.label}:</strong>{" "}
                <span
                  data-testid={`pet.biometrics.entry.${data?.id}.${item.testIdKey}-value`}
                >
                  {" "}
                  {item.value || ""}
                </span>
              </Typography>
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  return (
    <Grid container spacing={2} mb={3}>
      <Grid item xs={12} sm={4}>
        <Controller
          name="temperature"
          defaultValue={""}
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              label={t("BodyTemperature")}
              fullWidth
              inputProps={{ maxLength: 20 }}
              data-testid={`pet.biometrics.entry.${data?.id}.temperature-input`}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          name="heartRate"
          defaultValue={""}
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              label={t("Puls/Min")}
              fullWidth
              inputProps={{ maxLength: 20 }}
              data-testid={`pet.biometrics.entry.${data?.id}.heart-rate-input`}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          name="weight"
          defaultValue={""}
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              label={t("Mass")}
              fullWidth
              inputProps={{ maxLength: 20 }}
              data-testid={`pet.biometrics.entry.${data?.id}.weight-input`}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          name="breathingFrequency"
          defaultValue={""}
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              label={t("FrequencyOfBreathing")}
              fullWidth
              inputProps={{ maxLength: 20 }}
              data-testid={`pet.biometrics.entry.${data?.id}.breathing-frequency-input`}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          name="length"
          defaultValue={""}
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              label={t("length")}
              fullWidth
              inputProps={{ maxLength: 20 }}
              data-testid={`pet.biometrics.entry.${data?.id}.length-input`}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          name="height"
          defaultValue={""}
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              label={t("height")}
              fullWidth
              inputProps={{ maxLength: 20 }}
              data-testid={`pet.biometrics.entry.${data?.id}.height-input`}
            />
          )}
        />
      </Grid>
      <Grid
        item
        xs={12}
        display="flex"
        justifyContent={"flex-end"}
        alignItems={"center"}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={!isPermissionAllowed(EDIT_APPOINTMENT)}
          data-testid={`pet.biometrics.entry.${data?.id}.save-button`}
          sx={{ width: 150 }}
        >
          {t("Save")}
        </Button>
      </Grid>
    </Grid>
  );
}
